import {Application, ApplicationStatus} from '../types';

// FNP-1027: should the Decommission button MOUNT at all?
//
// ⚠️ A DIFFERENT QUESTION FROM `hasProvisionedInfrastructure`, which is why
// this is a separate predicate rather than a widening of that one.
//
//   hasProvisionedInfrastructure  — "does this record own infrastructure?"
//   isTeardownSubject             — "is this a plausible teardown subject?"
//
// The button was gated on the first, and a record with no `infraStackNames`
// answered false, so it never mounted. That removed the button in precisely
// the case FNP-1027 exists to handle: an operator looking at a CREATING record
// left behind by a provision that died, wanting to know what to do about it.
//
// The gate's own comment already argued for this — it called the check
// "deliberately the wider test of the two", said "a button that refuses with a
// reason beats a button that is not there", and cited FNP-1027 by name. The
// intent was right; the implementation returned false on a null
// `infraStackNames` and did not deliver it. Found by clicking the page, not by
// a test: FNP-1027's component tests were green on a fix no operator could
// reach, because jsdom mounted the component directly and the page never did.
//
// So the mount is decided by STATUS, and the refusal stays where it already
// lives — `getDecommissionEligibility` answers per-stage, with the
// never-provisioned remedy for this exact shape.
//
// ⚠️ TERMINAL STATES ARE EXCLUDED, and that is the bound on the widening. A
// DECOMMISSIONED record has already been torn down; offering a live
// Decommission button there invites a second cascade over the first, which is
// the race the three DELETING guards exist to prevent. DELETING is excluded
// for the same reason — a teardown is running right now.
//
// ACTIVE, INACTIVE, CREATING and FAILED all mount. Each is a record an
// operator may legitimately want torn down, and each gets a refusal with a
// reason when the teardown cannot proceed.
export function isTeardownSubject(record: Application): boolean {
    const status = record.applicationStatus;

    // An unset status is the legacy admin-created shape. Mount, and let the
    // eligibility gate speak — consistent with the "refuse with a reason"
    // rule, and it cannot start a teardown on its own.
    if (!status) {
        return true;
    }

    return status !== ApplicationStatus.DECOMMISSIONED
        && status !== ApplicationStatus.DELETING;
}
